import type { StockRow } from '../stock/public-api';
import { formatStockQuantity, getStatus } from './inventoryUtils';

export type InventoryStockStatusTone = 'ok' | 'warning' | 'critical';

/** True si la fecha de vencimiento ya pasó (misma comparación que getStatus/filtros). */
export function isStockExpiryPast(expiresAt: string): boolean {
  return new Date(expiresAt).getTime() < Date.now();
}

/**
 * `YYYY-MM-DD…` → `DD/MM` (mismo año) o `DD/MM/YYYY` (otro año).
 * Recorta la parte de fecha sin convertir TZ: evita el off-by-one de
 * `new Date('YYYY-MM-DD')` (medianoche UTC) en pantallas es-AR.
 */
export function formatStockExpiryDate(expiresAt: string): string {
  const iso = expiresAt.slice(0, 10);
  const [y, m, d] = iso.split('-');
  if (iso.length !== 10 || !y || !m || !d) return expiresAt;
  return y === String(new Date().getFullYear()) ? `${d}/${m}` : `${d}/${m}/${y}`;
}

const STATUS_LABELS: Record<ReturnType<typeof getStatus>, string> = {
  EXPIRED: 'Vencido',
  LOW: 'Stock bajo',
  EXPIRING: 'Vence pronto',
  NEGATIVE: 'Negativo',
  OK: 'OK',
};

export function getInventoryStockStatusLabel(
  row: Pick<StockRow, 'expiresAt' | 'quantity' | 'minQuantity'>
): string {
  // quantity 0 es "Sin stock" en todos lados (la card ya lo mostraba así;
  // antes el detalle podía decir "Stock bajo" para la misma fila cuando min=0).
  if (row.quantity === 0) return 'Sin stock';
  return STATUS_LABELS[getStatus(row)];
}

export function getInventoryStockStatusTone(
  row: Pick<StockRow, 'expiresAt' | 'quantity' | 'minQuantity'>
): InventoryStockStatusTone {
  if (row.quantity <= 0) return 'critical';
  const status = getStatus(row);
  if (status === 'NEGATIVE' || status === 'EXPIRED') return 'critical';
  if (status === 'LOW' || status === 'EXPIRING') return 'warning';
  return 'ok';
}

/** Etiqueta + tono + meta para cards y modales de stock. */
export function resolveInventoryStockStatusPresentation(row: StockRow): {
  label: string;
  tone: InventoryStockStatusTone;
  meta: string;
} {
  const label = getInventoryStockStatusLabel(row);
  const tone = getInventoryStockStatusTone(row);
  const metaParts: string[] = [];
  if (row.minQuantity > 0) {
    metaParts.push(`mín. ${formatStockQuantity(row.minQuantity, row.unitOfMeasure)}`);
  }
  if (row.expiresAt) {
    metaParts.push(
      isStockExpiryPast(row.expiresAt)
        ? `venció ${formatStockExpiryDate(row.expiresAt)}`
        : `vence ${formatStockExpiryDate(row.expiresAt)}`
    );
  }
  return { label, tone, meta: metaParts.join(' · ') };
}
